import { AuthController_signOut_v1 } from '@repo/api-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

import { useApiClient } from '@/api/api-client';

import { SessionContext, type SessionEnding, type SessionStore } from './session';
import type { SessionHolder } from './session-holder';

/**
 * The session, rendered.
 *
 * Everything about *holding* a session is in `session-holder.ts` and everything
 * about *keeping* one is in `session-storage.ts`. This file is only the two
 * things that need React: subscribing a render to the holder, and the one
 * behaviour a screen cannot implement for itself, which is signing out.
 *
 * Where a viewer goes when a session ends is not here. That is the route
 * guard's, and the note further down says why it moved.
 *
 * The holder is passed in rather than built here because the API client needs
 * the same one, and it is built before any of this renders. See `main.tsx`.
 */
export function SessionProvider({
  holder,
  children,
}: {
  holder: SessionHolder;
  children: ReactNode;
}) {
  const client = useApiClient();

  // The holder is the source of truth and it is not React state: the API client
  // reads the token from it on every request, including requests made from
  // outside any render. Subscribing is how a render finds out.
  const session = useSyncExternalStore(holder.subscribe, holder.read);

  const signOut = useCallback(async () => {
    try {
      // Sent while the token is still held, so the transport attaches it and
      // the server can actually end this session. No body and no path
      // parameters — the descriptor carries the verb and the path.
      //
      // The timeout is what makes `finally` reachable. `finally` runs when the
      // promise *settles*, and a rejection settles it — but a hang does not,
      // and browser `fetch` has no timeout of its own. A blackholed route, a
      // captive portal that never answers, or a wedged API host would leave
      // this pending indefinitely: no clear, no redirect, the shell still
      // rendered, and the token still in `sessionStorage` for the rest of its
      // twelve hours. That is the ordinary end of a shift on a shared desk with
      // the network out, and the next person at it inherits the session.
      //
      // Five seconds, because this is not a request whose answer anybody is
      // waiting to read. It surfaces as an `ApiNetworkError` rather than an
      // `AbortError` — `AbortSignal.timeout` aborts with a `TimeoutError`, which
      // the transport does not treat as the caller's own doing — and either way
      // it lands in the catch below and the local clear happens.
      //
      // Not the other shape: clearing locally first and letting the request
      // race would cost the property the paragraph above wants, which is the
      // token still being held when the request goes out.
      if (holder.token() !== undefined) {
        await client.request(AuthController_signOut_v1, { signal: AbortSignal.timeout(5_000) });
      }
    } catch {
      // Swallowed on purpose, and not logged: the failure is either the network
      // or a refusal, neither is actionable by the person who clicked, and the
      // one thing worth writing down is the one thing that must never be
      // written down. The local clear below happens regardless — that is the
      // whole contract of this function.
    } finally {
      // 'signed-out' and not 'expired' even when the request above came back
      // refused as expired: that path has already recorded its own reason, and
      // this one runs after it. What the person did outranks what the server
      // said about a token they were throwing away anyway.
      holder.clear('signed-out');
    }
  }, [client, holder]);

  // No redirect here any more, and its removal is FE006's, not an oversight.
  //
  // This file used to bounce to sign-in whenever a session went away — expired
  // mid-keystroke, or signed out — because until the route guard landed nothing
  // else could. The guard now answers that: a route declares whether it needs a
  // session, and the moment there is none it sends the viewer to sign-in
  // carrying the screen they were on, so they are returned to it afterwards.
  //
  // Two owners of one redirect was not merely redundant, it was wrong. Both ran
  // in the same commit — the guard's `Navigate` first, as the deeper component,
  // then this effect — so this one landed last and overwrote the destination
  // the guard had just recorded. Every expired session forgot where it was, and
  // the symptom was a user who signed back in and found themselves on the
  // dashboard instead of the screen they had been working on.
  //
  // What is lost with it: a session ending on a *public* route no longer moves
  // anybody. That is the correct answer — a public route is one that never
  // needed a session, so losing one changes nothing about whether it may be
  // seen.

  // Mirrored into React state rather than read from the holder inside the memo
  // below, and the reason is acknowledgement rather than tidiness. Clearing the
  // ending has to be able to cause a render: the holder's copy is not part of
  // the `useSyncExternalStore` snapshot — the session is already `null` and
  // stays `null` when it changes — so a memo reading the holder directly would
  // keep serving the acknowledged value until something unrelated re-rendered
  // this provider, and the next visit to sign-in would show the old reason.
  const [endedBecause, setEndedBecause] = useState<SessionEnding | null>(() =>
    holder.endedBecause(),
  );

  // Every path that records an ending also nulls the session, so this is the
  // moment one can have appeared.
  useEffect(() => {
    if (session === null) setEndedBecause(holder.endedBecause());
  }, [holder, session]);

  const acknowledgeEnding = useCallback(() => {
    holder.acknowledgeEnding();
    setEndedBecause(null);
  }, [holder]);

  const store = useMemo<SessionStore>(
    () => ({
      session,
      endedBecause,
      acknowledgeEnding,
      signedIn: holder.signedIn,
      credentialChanged: holder.credentialChanged,
      signOut,
    }),
    [acknowledgeEnding, endedBecause, holder, session, signOut],
  );

  return <SessionContext value={store}>{children}</SessionContext>;
}
